/**
 * O que sai do app para fora: planilhas e o resumo em texto para WhatsApp.
 */
import { calcular, centavos, fechamentoDaCompra, nomeDoLote, situacaoDePagamento, textoDosDescontos, type Fechamento, type GrupoDeDesconto, type Resultado } from './contas'
import { NOME_COMISSAO_CLIENTE, NOME_COMISSAO_COMPRADOR, NOME_FINANCEIRO, NOME_FUNRURAL, TIPOS_DE_PORTEIRA } from './tipos'
import { contasPorPorteira, divisaoDoPagamento, gastosDoGestor, linhasDaRevenda, nomeDaPorteira, porteirasDa, temApartacao, type Apartacao, type ContaDaPorteira } from './destinos'
import { comissaoPagaFora, contasDaFicha, resumirContas, type DadosFinanceiros } from './financeiro'
import { dataBR, diaDe, hora, kg, num, plural, quando, reais } from './numeros'
import type { Aba, Celula } from './planilha'
import { lotesParaVender } from './revenda'
import { BASES, SITUACOES, type Emitente, type Ficha, type Lote, type Produtor, type VendaDaPorteira } from './tipos'

const r2 = (n: number | null) => (n === null || !Number.isFinite(n) ? null : Math.round(n * 100) / 100)

export function lugar(x: { propriedade?: string; nome?: string; municipio?: string; uf?: string }): string {
  return [x.propriedade || x.nome, [x.municipio, x.uf].filter(Boolean).join('/')].filter(Boolean).join(', ')
}

/**
 * 3D · As GTAs da ficha: a anotada na abertura (ficha antiga, ou ficha sem
 * porteiras) e a de cada porteira. O romaneio do vendedor leva todas.
 */
export function gtasDa(f: Pick<Ficha, 'gta' | 'porteiras'>): string[] {
  const todas = [f.gta, ...porteirasDa(f).map((p) => p.gta)].map((g) => String(g ?? '').trim()).filter(Boolean)
  return [...new Set(todas)]
}

/** O destino do romaneio do vendedor: só o da ficha antiga. As porteiras não aparecem para ele (3B). */
export function destinoDoVendedor(f: Pick<Ficha, 'destino'>): string {
  return f.destino ? lugar(f.destino) : ''
}

/**
 * R1C · O nome do campo nos papéis internos: «Porteiras» quando a ficha tem
 * porteira (um nome só em todas as telas — nunca «destino» para a mesma
 * coisa); «Destino» só na ficha antiga, que anotou o destino na abertura.
 */
export function rotuloDoDestino(f: Pick<Ficha, 'destino' | 'porteiras'>): string {
  return destinoDoVendedor(f) ? 'Destino' : 'Porteiras'
}

/**
 * 3D · O destino nos papéis internos (Custo, Excel de várias fichas): o da
 * ficha antiga ou, desde que o destino virou porteira, os nomes delas.
 */
export function destinoInterno(f: Pick<Ficha, 'destino' | 'porteiras'>): string {
  return destinoDoVendedor(f) || porteirasDa(f).map((p) => lugar({ nome: nomeDaPorteira(p), municipio: p.municipio, uf: p.uf })).join('; ')
}

/**
 * 3D · As linhas de desconto do papel. Descontos iguais em todos os lotes: o
 * jeito de sempre, «Financeiro (2% do bruto)» e «Funrural (1,63% do bruto)».
 * Diferentes: uma linha por lote, com o % e o bruto dele — a conta que se
 * refaz à mão.
 */
export function linhasDeDesconto(x: {
  grupos: (Pick<GrupoDeDesconto, 'financeiroPct' | 'funruralPct' | 'bruto' | 'financeiro' | 'funrural' | 'lotes'>)[]
  descontosIguais: boolean; financeiro: number; funrural: number
}): { rotulo: string; valor: number }[] {
  if (x.descontosIguais) {
    const g = x.grupos[0]
    const out: { rotulo: string; valor: number }[] = []
    // R1C: os nomes do gestor (06/10/2026) — a conta é a mesma
    if (x.financeiro) out.push({ rotulo: `${NOME_FINANCEIRO} — ${num(g?.financeiroPct ?? 0, 2)}% do bruto`, valor: x.financeiro })
    if (x.funrural) out.push({ rotulo: `${NOME_FUNRURAL} — ${num(g?.funruralPct ?? 0, 2)}% do bruto`, valor: x.funrural })
    return out
  }
  return x.grupos.filter((g) => g.financeiro || g.funrural).map((g) => ({
    rotulo: `${g.lotes.map(nomeDoLote).join(', ')}: ${textoDosDescontos(g)} de ${reais(g.bruto)}`,
    valor: centavos(g.financeiro + g.funrural),
  }))
}

/**
 * R1C · A média de um lote conta só os animais PESADOS (revisão de
 * 06/10/2026). Quando há animal sem peso no lote, a frase diz quantos foram
 * pesados: «191,1 kg (14 pesados)». Todos pesados: só «191,1 kg».
 */
export function textoDaMedia(kgTotal: number, cab: number, cabPesadas: number): string | null {
  if (!cabPesadas || !kgTotal) return null
  const m = `${num(kgTotal / cabPesadas, 1)} kg`
  return cabPesadas < cab ? `${m} (${cabPesadas} pesado${cabPesadas === 1 ? '' : 's'})` : m
}

/**
 * R1C · O pagamento COMBINADO para o romaneio do vendedor: a forma e os
 * vencimentos das parcelas (aba Pagamento). Só o combinado — o que já foi
 * pago (quitação) nunca sai no papel. Nada combinado: texto vazio.
 */
export function textoDoCombinado(f: Partial<Pick<Ficha, 'condicao' | 'pagamentos'>>): string {
  const forma = f.condicao?.forma?.trim() ?? ''
  const parcelas = (Array.isArray(f.pagamentos) ? f.pagamentos : []).filter((p) => p.vencimento || p.valor)
    .slice().sort((a, b) => (a.vencimento || '9999').localeCompare(b.vencimento || '9999'))
  const venc = parcelas.length === 1
    ? (parcelas[0]!.vencimento ? `vencimento ${dataBR(parcelas[0]!.vencimento)}` : '')
    : parcelas.length > 1
      ? `${parcelas.length} parcelas: ${parcelas.map((p) => `${reais(p.valor)}${p.vencimento ? ' em ' + dataBR(p.vencimento) : ''}`).join('; ')}`
      : ''
  return [forma, venc].filter(Boolean).join(' · ')
}

/** R1C · Algum lote com quebra combinada? (o papel ganha as colunas da quebra) */
export function temQuebra(r: Pick<Resultado, 'linhas'>): boolean {
  return r.linhas.some((l) => l.cab > 0 && l.quebraPct > 0)
}

/** «03/10/2026» ou «03/10 a 05/10/2026» quando a ficha teve pesagem em mais de um dia. */
export function periodo(f: Ficha): string {
  const dias = [...new Set(f.registros.map((r) => diaDe(r.em)))].sort()
  if (dias.length <= 1) return dataBR(dias[0] ?? f.data)
  return `${dataBR(dias[0]).slice(0, 5)} a ${dataBR(dias[dias.length - 1])}`
}

/** As duas abas do romaneio de uma ficha: o resumo e a relação de pesagens. */
export function abasDoRomaneio(f: Ficha): Aba[] {
  const r = calcular(f)
  const em = f.emitente
  const L: Celula[][] = [
    [`Romaneio de pesagem nº ${f.numero}${f.versao > 1 ? ` — versão ${f.versao}` : ''}`],
    ['Data', periodo(f), 'Finalidade', f.finalidade, 'Situação', SITUACOES[f.situacao]],
    ['Produtor', f.produtor.nome, 'CPF/CNPJ', f.produtor.doc],
    ['Origem', lugar(f.produtor), 'GPS', f.produtor.gps ? `${f.produtor.gps.lat.toFixed(5)}, ${f.produtor.gps.lon.toFixed(5)}` : ''],
    ['Destino', destinoDoVendedor(f), 'GTA', gtasDa(f).join(', ')],
    ['Emitente', em?.nome ?? '', 'Telefone', em?.telefone ?? ''],
    // R1C: o pagamento combinado (forma e vencimento) — nunca a quitação
    ...(textoDoCombinado(f) ? [['Pagamento combinado', textoDoCombinado(f)]] : []),
    [],
  ]
  // R1C: com quebra, o papel mostra o peso da balança, a quebra e o peso que vale;
  // a coluna «Pesados» diz de quantos animais sai a média
  const q = temQuebra(r)
  L.push(['Lote', 'Base', 'Cabeças', q ? 'Peso da balança (kg)' : 'Peso total (kg)', 'Média (kg)', 'Pesados',
    ...(q ? ['Quebra (%)', 'Peso que vale (kg)'] : []), 'Rendimento (%)', 'Arrobas', 'Preço', 'Valor (R$)'])
  const ini = L.length - 1
  for (const l of r.linhas.filter((x) => x.cab > 0)) {
    L.push([l.lote.categoria, BASES[l.lote.base].nome, l.cab, r2(l.kg) || null, l.cabPesadas ? r2(l.kg / l.cabPesadas) : null, l.cabPesadas,
      ...(q ? [l.quebraPct || null, l.quebraPct ? r2(l.kgQueVale) : null] : []),
      l.lote.base === 'arroba' ? l.lote.rendimento : null, l.arrobas === null ? null : r2(l.arrobas), l.lote.preco, r2(l.valor)])
  }
  L.push(['Total', '', r.cab, r2(r.kg) || null, r2(r.media), r.cabPesadas, ...(q ? [null, null] : []), null, r.temArroba ? r2(r.arrobas) : null, null, r.temPreco ? r.bruto : null])
  const tot = L.length - 1
  if (r.temPreco) {
    L.push([], ['Valor bruto', r.bruto])
    for (const d of linhasDeDesconto(r)) L.push([d.rotulo, -d.valor])
    // por lote, o financeiro e o funrural da ficha em texto: número na coluna somaria duas vezes
    if (!r.descontosIguais) L.push([`Somados: desconto à vista ${reais(r.financeiro)} · desconto de Funrural ${reais(r.funrural)}`])
    L.push(['Valor líquido', r.liquido])
  }
  const liq = L.length - 1
  const P: Celula[][] = [['#', 'Data', 'Hora', 'Lote', 'Cabeças', 'Peso (kg)', 'Média (kg)', 'Vazio (kg)', 'Carregado (kg)', 'Observação']]
  for (const x of f.registros) {
    const lote = f.lotes.find((l) => l.id === x.loteId)
    P.push([x.n, dataBR(diaDe(x.em)), hora(x.em), lote ? nomeDoLote(lote) : '', x.pendente ? null : x.qtd, x.pendente ? null : x.kg,
      x.kg && x.qtd ? r2(x.kg / x.qtd) : null, x.tara, x.bruto, x.pendente ? 'aguardando carga' : x.obs])
  }
  return [
    { nome: 'Romaneio', linhas: L, larguras: [24, 22, 10, 16, 12, 9, ...(q ? [11, 16] : []), 15, 11, 11, 14], negrito: [0, ini, tot, ...(r.temPreco ? [liq] : [])] },
    { nome: 'Pesagens', linhas: P, larguras: [6, 12, 8, 28, 10, 11, 11, 11, 14, 30], negrito: [0] },
  ]
}

/**
 * Fechamento da compra (2E) para o escritório: uma linha por quem recebe, com
 * o que já foi pago, e o custo total por cabeça e por arroba. Recebe o
 * fechamento já montado para o papel de quem exporta: o do comprador vem sem
 * comissão, e então a planilha não tem a linha nem um total que a revele.
 */
export function abasDoFechamento(f: Ficha, fe: Fechamento, ap?: Apartacao): Aba[] {
  const r = calcular(f)
  const L: Celula[][] = [
    // R1C: o comprador não lê sobre comissão — o papel dele diz só «Total»
    [`Fechamento da compra — ficha nº ${f.numero}`],
    ['Data', periodo(f), 'Situação', SITUACOES[f.situacao]],
    ['Vendedor', f.produtor.nome, 'CPF/CNPJ', f.produtor.doc],
    ['Origem', lugar(f.produtor), rotuloDoDestino(f), destinoInterno(f)],
    ['Cabeças', r.cab, 'Peso total (kg)', r2(r.kg) || null, 'Arrobas', r.temArroba ? r2(r.arrobas) : null],
    [],
    // R2B: «Sua parte» é o que o gestor paga; a parte do cliente (intermediação) fica «a cargo do cliente»
    ['Para quem', 'O que é', 'Valor (R$)', 'Sua parte (R$)', 'Já pago (R$)', 'Falta pagar (R$)'],
  ]
  const ini = L.length - 1
  const descontos = !r.descontosIguais ? ['descontos por lote']
    : [r.financeiro ? `desconto à vista ${num(r.financeiroPct ?? 0, 2)}%` : '', r.funrural ? `Funrural ${num(r.funruralPct ?? 0, 2)}%` : ''].filter(Boolean)
  // 3F: a parte das porteiras Cliente (intermediação) o cliente paga direto: fica fora do «falta pagar»
  const doCliente = fe.vendedor.aCargoDoCliente ? `; a cargo do cliente ${reais(fe.vendedor.aCargoDoCliente)} (paga direto ao vendedor)` : ''
  L.push([`Vendedor — ${f.produtor.nome}`,
    `Líquido do romaneio (bruto ${reais(r.bruto)}${descontos.length ? ' − ' + descontos.join(' − ') : ''})${doCliente}`,
    fe.vendedor.valor, centavos(fe.vendedor.valor - fe.vendedor.aCargoDoCliente), fe.vendedor.pago, fe.vendedor.aPagar])
  if (fe.comissao) {
    L.push([NOME_COMISSAO_COMPRADOR, `${reais(fe.comissao.porCabeca)} por cabeça × ${fe.comissao.cab} cab${fe.comissao.acertada ? ' — paga no acerto' : ''}`, fe.comissao.valor, fe.comissao.valor,
      fe.comissao.pago || null, fe.comissao.falta])
  } else if (fe.comissaoPendente) {
    L.push([NOME_COMISSAO_COMPRADOR, 'pendente — ainda não lançada (fora do total)', null, null, null, null])
  }
  const nomeDe = (id?: string) => ap?.porteiras.find((p) => p.id === id)?.nome
  for (const g of fe.gastos) {
    const onde = ap && ap.porteiras.length > 1 ? ` — ${nomeDe(g.destinoId) ?? 'sem porteira (gastos rateados pelas cabeças)'}` : ''
    const sua = fe.doGestor[g.id] ?? g.valor
    // R2B: o gasto da porteira Cliente é do cliente — «a cargo do cliente», fora do que o gestor paga
    const cargo = sua === 0 ? ' — a cargo do cliente' : sua !== g.valor ? ` — a cargo do cliente ${reais(centavos(g.valor - sua))}` : ''
    L.push([`${g.descricao}${g.favorecido ? ` — ${g.favorecido}` : ''}`, `Gasto da ficha (valor total)${onde}${cargo}${g.pagoEm ? ` · pago em ${dataBR(g.pagoEm)}` : ''}`,
      g.valor, sua, g.pagoEm ? sua : null, g.pagoEm ? 0 : sua])
  }
  L.push([fe.semComissao ? 'Total' : 'Custo total', '', fe.total])
  const tot = L.length - 1
  // R2B: o custo do gestor separado da conferência do que é do cliente
  if (fe.vendedor.aCargoDoCliente > 0 || fe.custoDoCliente > 0) {
    L.push([fe.semComissao ? 'Sua parte' : 'Custo do gestor', 'vendedor (sua parte) + gastos seus' + (fe.semComissao ? '' : ' + comissão do comprador'), fe.custoDoGestor])
    L.push(['Custo do cliente (conferência)', 'a cargo do cliente: vendedor e gastos das porteiras Cliente — ele paga direto', fe.custoDoCliente])
  }
  L.push(['Falta pagar (sua parte)', '', fe.aPagar])
  if (fe.porCabeca !== null) L.push(['Por cabeça', '', r2(fe.porCabeca)])
  if (fe.porKg !== null) L.push(['Por kg', '', r2(fe.porKg)])
  if (fe.porArroba !== null) L.push(['Por arroba', '', r2(fe.porArroba)])
  // R1C: «custo pendente» não aparece para o comprador (fe.semComissao = papel do comprador)
  if (f.custoFechado) L.push([], [`Custo fechado em ${quando(f.custoFechado.em)} por ${f.custoFechado.por}`])
  else if (!fe.semComissao) L.push([], ['Custo pendente (ainda não conferido)'])
  const negrito = [0, ini, tot]
  // 3B: com apartação, uma linha por porteira — a soma bate com as linhas de cima
  if (ap && ap.porteiras.length > 1) {
    L.push([], ['POR PORTEIRA'])
    negrito.push(L.length - 1)
    L.push(['Porteira', 'Tipo', 'Cabeças', 'Peso (kg)', 'Arrobas', 'Vendedor (R$)', 'Gastos (R$)',
      ...(fe.semComissao ? [] : [`${NOME_COMISSAO_COMPRADOR} (R$)`]), 'Custo (R$)', 'Por cabeça (R$)',
      // 3F: venda e ágio só existem na versão do gestor e da Consulta — nem a coluna vai para o comprador
      ...(fe.semComissao ? [] : [`${NOME_COMISSAO_CLIENTE} (R$)`]), 'O cliente paga (R$)', ...(fe.semComissao ? [] : ['Venda (R$)', 'Ágio (R$)'])])
    negrito.push(L.length - 1)
    for (const p of ap.porteiras) {
      L.push([p.nome, p.porteira ? TIPOS_DE_PORTEIRA[p.porteira.tipo].nome : '', p.cab, r2(p.kg) || null, p.temArroba ? r2(p.arrobas) : null,
        p.liquido, p.totalGastos, ...(fe.semComissao ? [] : [p.comissaoPagar?.valor ?? null]), p.custo, r2(p.porCabeca),
        ...(fe.semComissao ? [] : [p.comissaoReceber?.valor ?? null]), p.totalDoCliente,
        ...(fe.semComissao ? [] : [p.venda?.valor ?? null, p.venda?.agio ?? null])])
    }
    if (ap.gastosSemPorteira) L.push([`Gastos rateados: os sem porteira (${reais(ap.gastosSemPorteira)}) foram divididos pelas cabeças de cada porteira.`])
    // 3F: o bloco de cada porteira Revenda — compra, venda, gastos, comissão e ágio
    if (!fe.semComissao) {
      for (const p of ap.porteiras) {
        const ls = linhasDaRevenda(p)
        if (!ls.length) continue
        L.push([], [`REVENDA — ${p.porteira?.nome || p.nome}`, 'Total (R$)', 'Por cabeça (R$)', 'Por arroba (R$)'])
        negrito.push(L.length - 1)
        for (const x of ls) L.push([x.rotulo, x.total, r2(x.porCabeca), r2(x.porArroba)])
        negrito.push(L.length - 1)
        if (p.venda?.conta.semPreco.length) L.push([`Sem preço de venda: ${p.venda.conta.semPreco.join(', ')} (fora da venda).`])
      }
    }
  }
  return [{ nome: 'Fechamento', linhas: L, larguras: [30, 60, 15, 15, 15, 16, 14, 16, 16, 14, 16, 18, 16], negrito }]
}

/**
 * 3B · O romaneio e fechamento do CLIENTE: só a parte dele, para ele pagar.
 * Lotes e pesagens da porteira dele, o valor ao vendedor (com o mesmo
 * financeiro e funrural da ficha, proporcional), os gastos da porteira e a
 * comissão do gestor. Quem não vê comissão (comprador) exporta sem ela.
 */
export function abasDoCliente(f: Ficha, p: ContaDaPorteira, semComissao: boolean, empresa?: string): Aba[] {
  const pc = pagamentoDoCliente(f, p, semComissao, empresa)
  const L: Celula[][] = [
    [`Romaneio e fechamento — ${p.nome} — ficha nº ${f.numero}`],
    ['Data', periodo(f)],
    // 4.2: o gestor é o vendedor para o cliente; o produtor de origem não aparece
    ['Vendedor', pc.empresa],
    ['Comprador', p.nome, 'Município', [p.porteira?.municipio, p.porteira?.uf].filter(Boolean).join('/')],
    // 3D: só a GTA da porteira dele — cada destino tem a sua
    ['GTA', p.porteira?.gta?.trim() ?? ''],
    [],
    ['Lote', 'Base', 'Cabeças', 'Peso total (kg)', 'Média (kg)', 'Rendimento (%)', 'Arrobas', 'Preço', 'Valor (R$)'],
  ]
  const ini = L.length - 1
  for (const l of p.linhas) {
    L.push([l.lote.categoria, BASES[l.lote.base].nome, l.cab, r2(l.kg) || null, l.cabPesadas ? r2(l.kg / l.cabPesadas) : null,
      l.lote.base === 'arroba' ? l.lote.rendimento : null, l.arrobas === null ? null : r2(l.arrobas), l.lote.preco, l.valor])
  }
  L.push(['Total', '', p.cab, r2(p.kg) || null, r2(p.media), null, p.temArroba ? r2(p.arrobas) : null, null, p.temPreco ? p.bruto : null])
  const tot = L.length - 1
  // R2B (revisão de 06/10/2026): dois blocos, cada um com o destinatário — sem total único
  L.push([], [pc.aoVendedor.titulo, 'R$'])
  const cab2 = L.length - 1
  if (p.temPreco) {
    L.push(['Valor bruto', p.bruto])
    for (const d of linhasDeDesconto(p)) L.push([d.rotulo, -d.valor])
  }
  L.push(['Valor do gado', pc.aoVendedor.valor])
  const fimVendedor = L.length - 1
  let cabD = -1, fimD = -1
  if (pc.direto.linhas.length) {
    L.push([], [pc.direto.titulo, 'R$'])
    cabD = L.length - 1
    for (const x of pc.direto.linhas) L.push([x.rotulo, x.valor])
    L.push(['Pague direto (total)', pc.direto.valor])
    fimD = L.length - 1
  }
  L.push([], [pc.aEmpresa.titulo, 'R$'])
  const cab3 = L.length - 1
  for (const x of pc.aEmpresa.linhas) L.push([x.rotulo, x.valor])
  L.push([`Pague a ${pc.empresa}`, pc.aEmpresa.valor])
  const fim = L.length - 1
  const P: Celula[][] = [['#', 'Data', 'Hora', 'Lote', 'Cabeças', 'Peso (kg)', 'Média (kg)', 'Observação']]
  for (const x of p.registros) {
    const lote = f.lotes.find((l) => l.id === x.loteId)
    P.push([x.n, dataBR(diaDe(x.em)), hora(x.em), lote ? nomeDoLote(lote) : '', x.pendente ? null : x.qtd, x.pendente ? null : x.kg,
      x.kg && x.qtd ? r2(x.kg / x.qtd) : null, x.pendente ? 'aguardando carga' : x.obs])
  }
  return [
    { nome: 'Cliente', linhas: L, larguras: [52, 22, 10, 16, 12, 15, 11, 11, 14], negrito: [0, ini, tot, cab2, fimVendedor, cab3, fim, ...(cabD >= 0 ? [cabD, fimD] : [])] },
    { nome: 'Pesagens', linhas: P, larguras: [6, 12, 8, 28, 10, 11, 11, 30], negrito: [0] },
  ]
}

/**
 * R2B · O que o CLIENTE paga, em dois blocos com o destinatário (a revisão
 * achou «Total a pagar» sem dizer para quem):
 *  - «Pague direto ao vendedor <nome>»: o valor ao vendedor da porteira dele;
 *  - «Pague direto: frete e gastos da sua porteira»: o cliente paga direto ao
 *    transportador (decisão do gestor, 06/10/2026 — na intermediação o frete é
 *    responsabilidade de quem compra; não passa pela empresa);
 *  - «Pague a <empresa>»: só o que a empresa adiantou (gastos rateados, os sem
 *    porteira) e a Comissão do cliente (quem não vê comissão imprime sem ela).
 * Sem total único: são pagamentos para pessoas diferentes. Tela, PDF e Excel.
 */
export interface PagamentoDoCliente {
  empresa: string
  aoVendedor: { titulo: string; valor: number }
  /** frete e gastos lançados na porteira do cliente: ele paga direto a quem fez o serviço */
  direto: { titulo: string; linhas: { rotulo: string; valor: number }[]; valor: number }
  aEmpresa: { titulo: string; linhas: { rotulo: string; valor: number }[]; valor: number }
}

export function pagamentoDoCliente(f: Pick<Ficha, 'produtor' | 'emitente'>, p: ContaDaPorteira, semComissao: boolean, empresa?: string): PagamentoDoCliente {
  const nome = empresa?.trim() || f.emitente?.nome?.trim() || 'a empresa'
  const diretas = p.gastos.map((g) => ({ rotulo: `${g.descricao}${g.favorecido ? ` — ${g.favorecido}` : ''}`, valor: g.valor }))
  const linhas: { rotulo: string; valor: number }[] = [
    ...(p.gastosRateados ? [{ rotulo: 'Gastos rateados (os sem porteira, divididos pelas cabeças)', valor: p.gastosRateados }] : []),
    ...(!semComissao && p.comissaoReceber
      ? [{ rotulo: `Comissão (${reais(p.comissaoReceber.porCabeca)} por cabeça × ${p.comissaoReceber.cab} cab)`, valor: p.comissaoReceber.valor }] : []),
  ]
  return {
    empresa: nome,
    // 4.2 (decisão do gestor, 06/10/2026): «as pontas não se comunicam» — o
    // papel do cliente mostra o gestor como VENDEDOR e nunca o produtor de origem
    aoVendedor: { titulo: `Valor do gado (pagamento conforme combinado com ${nome})`, valor: p.liquido },
    direto: { titulo: 'Pague direto: frete e gastos da sua porteira', linhas: diretas, valor: centavos(diretas.reduce((s, x) => s + x.valor, 0)) },
    aEmpresa: {
      titulo: `Pague a ${nome}: ${semComissao ? 'gastos adiantados' : 'gastos adiantados e comissão'}`, linhas,
      valor: centavos(linhas.reduce((s, x) => s + x.valor, 0)),
    },
  }
}

/* ---------------------------------------------------------------------------
 * 3F · Romaneio de VENDA (porteira Revenda) — para o comprador final
 * ------------------------------------------------------------------------- */

export interface RomaneioDeVenda {
  conta: ContaDaPorteira
  /** a conta lote a lote aos preços da VENDA (os mesmos pesos da compra) */
  resultado: Resultado
  /** os lotes da venda: categoria da compra, base, preço, rendimento e descontos da venda */
  lotes: Lote[]
  /** porteira antiga («Revenda direta» da 3B): o valor total combinado, sem preço por lote */
  valorTotal: number | null
  gta: string
  relacao: ReturnType<typeof relacaoDePesagens>
}

/**
 * Os números do romaneio de venda de uma porteira Revenda: SÓ os animais
 * dela, aos preços e descontos da venda, e só a GTA dela. Nada do preço de
 * compra: os lotes são os da tabela de venda, e até o rótulo curto da relação
 * de pesagens sai do preço de VENDA (o da compra iria junto quando dois lotes
 * têm a mesma categoria). Nada de ágio. `null` se a porteira não é Revenda ou
 * ainda não tem venda combinada.
 */
export function romaneioDeVenda(f: Pick<Ficha, 'lotes' | 'arrobaEmDuasCasas'>, p: ContaDaPorteira, venda: Pick<VendaDaPorteira, 'lotes'> | null | undefined): RomaneioDeVenda | null {
  if (p.porteira?.tipo !== 'revenda' || !p.venda) return null
  const lotes = lotesParaVender(f.lotes, venda ?? null)
  const resultado = p.venda.conta.resultado ?? calcular({ lotes, registros: p.registros, financeiroPct: 0, funruralPct: 0, arrobaEmDuasCasas: f.arrobaEmDuasCasas })
  return {
    conta: p, resultado, lotes,
    valorTotal: p.venda.conta.modo === 'total' ? p.venda.conta.valor : null,
    gta: p.porteira.gta?.trim() ?? '',
    relacao: relacaoDePesagens({ lotes, registros: p.registros }),
  }
}

/** O romaneio de venda em Excel: o resumo (vendedor = a empresa; comprador = o comprador final) e as pesagens da porteira. */
/** R2B · O número do romaneio de VENDA leva «-V»: não se confunde com o de compra da mesma ficha. */
export function numeroDaVenda(numero: string): string {
  return `${numero}-V`
}

export function abasDoRomaneioDeVenda(f: Ficha, rv: RomaneioDeVenda, comprador: Partial<Produtor>, em: Emitente | null): Aba[] {
  const r = rv.resultado
  const L: Celula[][] = [
    [`Romaneio de venda nº ${numeroDaVenda(f.numero)}`],
    ['Data', periodo(f), 'Situação', SITUACOES[f.situacao]],
    ['Vendedor', em?.nome ?? '', 'CPF/CNPJ', em?.doc ?? ''],
    ['Comprador', comprador.nome ?? '', 'CPF/CNPJ', comprador.doc ?? ''],
    ['Destino', lugar(comprador), 'GTA', rv.gta],
    [],
    ['Lote', 'Base', 'Cabeças', 'Peso total (kg)', 'Média (kg)', 'Rendimento (%)', 'Arrobas', 'Preço', 'Valor (R$)'],
  ]
  const ini = L.length - 1
  for (const l of r.linhas.filter((x) => x.cab > 0)) {
    L.push([l.lote.categoria, BASES[l.lote.base].nome, l.cab, r2(l.kg) || null, l.cabPesadas ? r2(l.kg / l.cabPesadas) : null,
      l.lote.base === 'arroba' ? l.lote.rendimento : null, l.arrobas === null ? null : r2(l.arrobas), l.lote.preco, r2(l.valor)])
  }
  L.push(['Total', '', r.cab, r2(r.kg) || null, r2(r.media), null, r.temArroba ? r2(r.arrobas) : null, null, r.temPreco ? r.bruto : null])
  const tot = L.length - 1
  if (r.temPreco) {
    L.push([], ['Valor bruto', r.bruto])
    for (const d of linhasDeDesconto(r)) L.push([d.rotulo, -d.valor])
    if (!r.descontosIguais) L.push([`Somados: desconto à vista ${reais(r.financeiro)} · desconto de Funrural ${reais(r.funrural)}`])
    L.push(['Valor líquido', r.liquido])
  } else if (rv.valorTotal !== null) {
    L.push([], ['Valor total combinado', rv.valorTotal])
  }
  const liq = L.length - 1
  const P: Celula[][] = [['Lote', 'Cabeças', 'Peso (kg)']]
  for (const x of rv.relacao.linhas) P.push([x.lote, x.qtd, x.kg])
  return [
    { nome: 'Romaneio de venda', linhas: L, larguras: [24, 22, 10, 16, 12, 15, 11, 11, 14], negrito: [0, ini, tot, liq] },
    { nome: 'Pesagens', linhas: P, larguras: [28, 10, 11], negrito: [0] },
  ]
}

/**
 * Várias fichas numa planilha: uma linha por ficha, e todas as pesagens na segunda aba.
 *
 * R2B · A MESMA REGRA DO PAINEL DAS FICHAS (`contasDaFicha`, financeiro.ts): só
 * ficha encerrada tem «a pagar» ou «a receber»; a Compra deve ao vendedor (a
 * sua parte), a Venda tem a receber do comprador; fretes e gastos sem «pago
 * em» entram no «a pagar»; com comissão (gestor e Consulta), as duas comissões
 * e a revenda também. Conferência: nem um nem outro.
 */
export function abasDasFichas(
  fichas: Ficha[],
  d: DadosFinanceiros = { quitacoes: [] },
  // o custo total tem de bater com a aba Custo: com comissão para gestor e
  // Consulta; para o comprador a coluna nem existe e o total sai sem ela
  o: { ver: boolean } = { ver: false },
): Aba[] {
  const F: Celula[][] = [['Número', 'Data', 'Situação', 'Finalidade', 'Produtor', 'CPF/CNPJ', 'Origem', 'Porteiras / destino', 'GTA',
    'Cabeças', 'Peso total (kg)', 'Média (kg)', 'Arrobas', 'Bruto (R$)', 'Desconto à vista (R$)', 'Desconto de Funrural (R$)', 'Líquido (R$)',
    'Pago ao vendedor / recebido do comprador (R$)', 'A pagar (R$)', 'A receber (R$)', 'A cargo do cliente (R$)', 'Próximo vencimento', 'Gastos internos (R$)',
    ...(o.ver ? ['Comissão do comprador (R$)', 'Custo total (R$)'] : ['Custo total (R$)'])]]
  const P: Celula[][] = [['Número', 'Produtor', '#', 'Data', 'Hora', 'Lote', 'Cabeças', 'Peso (kg)', 'Vazio (kg)', 'Carregado (kg)', 'Observação']]
  let totalPagar = 0, totalReceber = 0
  for (const f of fichas) {
    const r = calcular(f), dv = divisaoDoPagamento(f)
    const qs = d.quitacoes.filter((q) => q.fichaId === f.id) as Parameters<typeof situacaoDePagamento>[2]
    const pg = situacaoDePagamento(f, f.finalidade === 'Compra' ? dv.doGestor : r.liquido, qs)
    const contas = contasDaFicha(f, d, { verComissao: o.ver })
    const res = resumirContas(contas)
    totalPagar += Math.round(res.aPagar * 100); totalReceber += Math.round(res.aReceber * 100)
    const comissao = o.ver ? d.comissoes?.[f.id] ?? null : null
    const fe = fechamentoDaCompra(f, r, { verComissao: o.ver, comissao, quitacoes: qs, aCargoDoCliente: dv.aCargoDoCliente,
      gastosDoGestor: gastosDoGestor(f), comissaoPagaFora: comissaoPagaFora(d, f.id) })
    const encerrada = f.situacao === 'encerrada'
    const proxima = contas.filter((c) => c.falta > 0 && c.vencimento).map((c) => c.vencimento!).sort()[0] ?? null
    F.push([f.numero, periodo(f), SITUACOES[f.situacao], f.finalidade, f.produtor.nome, f.produtor.doc, lugar(f.produtor), destinoInterno(f), gtasDa(f).join(', '),
      r.cab, r2(r.kg) || null, r2(r.media), r.temArroba ? r2(r.arrobas) : null,
      r.temPreco ? r.bruto : null, r.temPreco ? r.financeiro : null, r.temPreco ? r.funrural : null, r.temPreco ? r.liquido : null,
      pg.pago || null, encerrada && res.aPagar ? res.aPagar : null, encerrada && res.aReceber ? res.aReceber : null,
      encerrada && dv.aCargoDoCliente ? dv.aCargoDoCliente : null, dataBR(proxima), fe.totalGastos || null,
      ...(o.ver ? [fe.comissao?.valor ?? null] : []), r.temPreco ? fe.total : null])
    for (const x of f.registros) {
      const lote = f.lotes.find((l) => l.id === x.loteId)
      P.push([f.numero, f.produtor.nome, x.n, dataBR(diaDe(x.em)), hora(x.em), lote ? nomeDoLote(lote) : '', x.pendente ? null : x.qtd,
        x.kg, x.tara, x.bruto, x.pendente ? 'aguardando carga' : x.obs])
    }
  }
  F.push([`Total (${fichas.length} ficha${fichas.length === 1 ? '' : 's'})`, '', '', '', '', '', '', '', '', null, null, null, null, null, null, null, null, null,
    totalPagar / 100, totalReceber / 100])
  const tot = F.length - 1
  F.push([], ['«A pagar» e «a receber» só em ficha encerrada: a Compra deve ao vendedor (a sua parte), a Venda tem a receber do comprador, e os fretes e gastos sem «pago em» estão a pagar. A mesma conta das Fichas, do Pagamento e da tela Contas.'])
  return [
    { nome: 'Fichas', linhas: F, larguras: [14, 12, 13, 12, 26, 16, 28, 28, 12, 9, 13, 10, 10, 13, 13, 13, 13, 16, 12, 12, 14, 14, 14, 15, 15], negrito: [0, tot] },
    { nome: 'Pesagens', linhas: P, larguras: [11, 26, 5, 11, 7, 28, 9, 10, 10, 13, 30], negrito: [0] },
  ]
}

/** Resumo em texto para colar no WhatsApp. */
export function textoDoResumo(f: Ficha): string {
  const r = calcular(f)
  const L: string[] = []
  L.push(`*Romaneio de pesagem nº ${f.numero}*${f.versao > 1 ? ` (versão ${f.versao})` : ''}`)
  L.push(`${periodo(f)} · ${f.finalidade}${f.situacao === 'aberta' ? ' · EM ANDAMENTO' : f.situacao === 'cancelada' ? ' · CANCELADA' : ''}`)
  L.push(`Produtor: ${f.produtor.nome}`)
  // 3D: sem destino informado (ficha nova), a linha fica só com a origem
  const destino = destinoDoVendedor(f)
  L.push(`Origem: ${lugar(f.produtor) || '—'}${destino ? ` → Destino: ${destino}` : ''}`)
  const gtas = gtasDa(f)
  if (gtas.length) L.push(`GTA nº ${gtas.join(', ')}`)
  const combinado = textoDoCombinado(f)
  if (combinado) L.push(`Pagamento combinado: ${combinado}`)
  L.push('')
  for (const l of r.linhas.filter((x) => x.cab > 0)) {
    // R1C: plural certo («53 bois», «20 vacas») e média só dos pesados
    const partes = [`${l.cab} ${plural(l.lote.categoria.toLowerCase(), l.cab)}`]
    const media = textoDaMedia(l.kg, l.cab, l.cabPesadas)
    if (l.kg) partes.push(`${kg(l.kg)} kg`)
    if (media) partes.push(`média ${media}`)
    if (l.quebraPct) partes.push(`quebra ${num(l.quebraPct, 2)}% → ${kg(l.kgQueVale)} kg`)
    if (l.arrobas !== null) partes.push(`${num(l.arrobas, 2)} @ (${num(l.lote.rendimento, 1)}%)`)
    if (l.lote.preco) partes.push(`a ${reais(l.lote.preco)}${BASES[l.lote.base].unidade.replace('R$', '')}`)
    if (l.valor !== null) partes.push(`= ${reais(l.valor)}`)
    L.push(partes.join(' · '))
  }
  L.push('')
  L.push(`*Total: ${r.cab} cab${r.kg ? ` · ${kg(r.kg)} kg` : ''}*`)
  if (r.temPreco) {
    L.push(`Bruto: ${reais(r.bruto)}`)
    for (const d of linhasDeDesconto(r)) L.push(`${d.rotulo}: − ${reais(d.valor)}`)
    L.push(`*Líquido: ${reais(r.liquido)}*`)
  }
  if (f.pesadoPor) L.push('', `Pesado por: ${f.pesadoPor}`)
  if (f.emitente?.nome) L.push(`${f.emitente.nome}${f.emitente.telefone ? ' · ' + f.emitente.telefone : ''}`)
  return L.join('\n')
}

/* ---------------------------------------------------------------------------
 * 3E · Prévia da pesagem e relação de pesagens que economiza papel
 * ------------------------------------------------------------------------- */

export interface LinhaDaPrevia {
  rotulo: string
  cab: number
  kg: number
  /** cabeças com peso — a média só olha para elas */
  cabPesadas: number
  media: number | null
}

export interface Previa {
  /** uma linha por lote (categoria e preço) com animal */
  lotes: LinhaDaPrevia[]
  total: LinhaDaPrevia
  /** com porteiras (duas ou mais): a mesma tabela para cada uma */
  porteiras: { nome: string; lotes: LinhaDaPrevia[]; total: LinhaDaPrevia }[]
}

function linhaDaPrevia(rotulo: string, cab: number, kg: number, cabPesadas: number): LinhaDaPrevia {
  return { rotulo, cab, kg, cabPesadas, media: cabPesadas ? kg / cabPesadas : null }
}

function totalDa(linhas: LinhaDaPrevia[]): LinhaDaPrevia {
  const cab = linhas.reduce((s, l) => s + l.cab, 0)
  const kg = linhas.reduce((s, l) => s + l.kg, 0)
  const pesadas = linhas.reduce((s, l) => s + l.cabPesadas, 0)
  // média do total só quando TODOS os animais têm peso (regra do CLAUDE.md:
  // 2 bois pesados entre 26 cabeças não dão média de nada)
  return { rotulo: 'Total', cab, kg, cabPesadas: pesadas, media: pesadas && pesadas === cab ? kg / pesadas : null }
}

/**
 * A «Prévia» da tela de pesagem (pedido do gestor no test drive de 06/10/2026:
 * «extratificar a quantidade e as médias por categoria; não está evidente o
 * que é garrote e o que é bezerro»). Por lote: cabeças, peso total e média,
 * e o total. Com porteiras, também uma tabela por porteira. Caminhão
 * aguardando carga fica de fora, como em todos os totais.
 */
export function previaDaPesagem(f: Pick<Ficha, 'lotes' | 'registros' | 'financeiroPct' | 'funruralPct' | 'custos' | 'porteiras'>): Previa {
  const r = calcular(f)
  const lotes = r.linhas.filter((l) => l.cab > 0).map((l) => linhaDaPrevia(nomeDoLote(l.lote), l.cab, l.kg, l.cabPesadas))
  const porteiras = temApartacao(f)
    ? contasPorPorteira(f, { verComissao: false }).porteiras.map((p) => {
      const ls = p.linhas.filter((l) => l.cab > 0).map((l) => linhaDaPrevia(nomeDoLote(l.lote), l.cab, l.kg, l.cabPesadas))
      return { nome: p.nome, lotes: ls, total: totalDa(ls) }
    })
    : []
  return { lotes, total: totalDa(lotes), porteiras }
}

/**
 * O nome curto de cada lote, para a relação de pesagens do papel: só a
 * categoria quando ela aparece num lote só; com dois lotes da mesma
 * categoria, o preço junto («Bezerro 1.900» e «Bezerro 1.600»).
 */
export function rotulosCurtos(lotes: Lote[]): Map<string, string> {
  const porCategoria = new Map<string, number>()
  for (const l of lotes) porCategoria.set(l.categoria, (porCategoria.get(l.categoria) ?? 0) + 1)
  const out = new Map<string, string>()
  const usados = new Set<string>()
  for (const l of lotes) {
    let r = l.categoria
    if ((porCategoria.get(l.categoria) ?? 0) > 1) {
      r = `${l.categoria} ${l.preco ? num(l.preco, l.preco % 1 ? 2 : 0) : 's/ preço'}`
      // mesma categoria e mesmo preço (bases diferentes): um número no fim
      let n = 2
      const base = r
      while (usados.has(r)) r = `${base} (${n++})`
    }
    usados.add(r)
    out.set(l.id, r)
  }
  return out
}

/**
 * 3E · A relação de pesagens do romaneio, enxuta para economizar papel: só
 * lote (curto), quantidade e peso. Sem data, hora e número de ordem por linha
 * (a data vai uma vez só, no cabeçalho, como período). Quando todo registro é
 * de uma cabeça, a coluna de quantidade some.
 */
export function relacaoDePesagens(f: Pick<Ficha, 'lotes' | 'registros'>): { mostraQtd: boolean; linhas: { id: string; lote: string; qtd: number | null; kg: number | null }[] } {
  const rotulos = rotulosCurtos(f.lotes)
  const linhas = f.registros.map((x) => ({
    id: x.id,
    lote: rotulos.get(x.loteId) ?? '?',
    qtd: x.pendente ? null : x.qtd,
    kg: x.pendente ? null : x.kg,
  }))
  return { mostraQtd: f.registros.some((x) => x.qtd !== 1), linhas }
}
